import { adminCarbonCropsUpdateStatement } from "portal/views/admin/permissions";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import { carbonCropsEndpoints } from "portal/state/portal/carbonCrops";
import { Field, Form, Formik } from "formik";
import { getAdminCarbonCropPath } from "portal/utils/routing";
import { GlobalHotKeys } from "react-hotkeys";
import { object, string } from "yup";
import { skipToken } from "@reduxjs/toolkit/query";
import { TextField } from "formik-mui";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { VeselkaCropAssociationsField } from "./VeselkaCropAssociationsField";
import { withAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import AddIcon from "@mui/icons-material/AddOutlined";
import React, { FunctionComponent, useState } from "react";

type CreateCarbonCropDraft = {
  name: string;
  veselkaCropIds: string[];
};

const emptyCreateDraft = (): CreateCarbonCropDraft => ({
  name: "",
  veselkaCropIds: [],
});

interface NewCarbonCropProps {
  hotkey?: string;
  onCreated?: () => void;
}

const _NewCarbonCrop: FunctionComponent<NewCarbonCropProps> = ({
  hotkey,
  onCreated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);

  const {
    data: unassociatedVeselkaCrops,
    isFetching: loadingVeselkaCrops,
    isError: loadingVeselkaCropsError,
  } = useQueryPopups(
    carbonCropsEndpoints.listUnassociatedVeselkaCrops.useQuery(
      isOpen ? {} : skipToken
    )
  );

  const [createCarbonCrop] = useMutationPopups(
    carbonCropsEndpoints.createCarbonCrop.useMutation(),
    {
      success: t("utils.actions.createdLong", {
        subject: t("models.carbonCrops.carbonCrop_title_one"),
      }),
    }
  );

  const veselkaCropOptions = unassociatedVeselkaCrops?.crops ?? [];

  return (
    <>
      {hotkey && (
        <GlobalHotKeys
          keyMap={{
            NEW_CARBON_CROP: {
              name: t("utils.actions.newLong", {
                subject: t("models.carbonCrops.carbonCrop_title_one"),
              }),
              action: "keyup",
              sequence: hotkey,
            },
          }}
          handlers={{
            NEW_CARBON_CROP: () => setOpen(true),
          }}
        />
      )}
      <Button startIcon={<AddIcon />} onClick={() => setOpen(true)}>
        <span className="hidden sm:inline">
          {t("utils.actions.newLong", {
            subject: t("models.carbonCrops.carbonCrop_title_one"),
          })}
        </span>
        <span className="sm:hidden">{t("utils.actions.new")}</span>
      </Button>
      <Formik<CreateCarbonCropDraft>
        enableReinitialize
        initialValues={emptyCreateDraft()}
        validationSchema={object({
          name: string().required(),
        })}
        onSubmit={async (values, { resetForm }) => {
          const { data, error } = await createCarbonCrop({
            name: values.name.trim(),
            veselkaCropIds: values.veselkaCropIds,
          });
          if (error) {
            return;
          }
          setOpen(false);
          resetForm();
          onCreated?.();
          navigate(getAdminCarbonCropPath(data.uuid));
        }}
      >
        {({
          submitForm,
          isSubmitting,
          dirty,
          resetForm,
          values,
          setFieldValue,
        }) => (
          <Dialog
            open={isOpen}
            onClose={() => {
              if (isSubmitting) {
                return;
              }
              setOpen(false);
              resetForm();
            }}
          >
            <DialogTitle>
              {t("utils.actions.newLong", {
                subject: t("models.carbonCrops.carbonCrop_title_one"),
              })}
            </DialogTitle>
            <Form>
              <DialogContent className="flex flex-col gap-4 pt-2">
                <Field
                  component={TextField}
                  name="name"
                  label={t("models.carbonCrops.fields.name")}
                  disabled={isSubmitting}
                />
                <VeselkaCropAssociationsField
                  value={values.veselkaCropIds}
                  onChange={(veselkaCropIds) =>
                    setFieldValue("veselkaCropIds", veselkaCropIds)
                  }
                  options={veselkaCropOptions}
                  loading={loadingVeselkaCrops}
                  loadingError={loadingVeselkaCropsError}
                  disabled={isSubmitting}
                />
                {isSubmitting && (
                  <div className="flex flex-col gap-2">
                    <Typography variant="body2" color="textSecondary">
                      {t(
                        "components.carbonCrop.NewCarbonCrop.savingAndGeneratingTranslations"
                      )}
                    </Typography>
                    <LinearProgress />
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  color="inherit"
                  disabled={isSubmitting}
                  onClick={() => {
                    setOpen(false);
                    resetForm();
                  }}
                >
                  {t("utils.actions.cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!dirty || isSubmitting}
                  loading={isSubmitting}
                  onClick={submitForm}
                  startIcon={<AddIcon />}
                >
                  {t("utils.actions.create")}
                </Button>
              </DialogActions>
            </Form>
          </Dialog>
        )}
      </Formik>
    </>
  );
};

export const NewCarbonCrop = withAuthorizationRequired(
  adminCarbonCropsUpdateStatement,
  _NewCarbonCrop
);
